<template>
  <v-container
    fluid
    class="pa-6"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <div>
          <div class="headline">
            Notifications
          </div>

          <div class="caption grey--text">
            Review invoice payment notifications and system events.
          </div>
        </div>

        <v-spacer />

        <v-btn
          color="primary"
          :disabled="unreadCount === 0 || loading"
          @click="markAllAsRead"
        >
          Mark all as read
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="filters.search"
              label="Search"
              placeholder="Invoice number, company, message..."
              outlined
              dense
              clearable
              prepend-inner-icon="mdi-magnify"
              @input="handleSearchInput"
              @click:clear="handleSearchClear"
            />
          </v-col>

          <v-col
            cols="12"
            md="3"
          >
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="Status"
              outlined
              dense
              clearable
              @change="reloadFirstPage"
            />
          </v-col>

          <v-col
            cols="12"
            md="3"
          >
            <v-select
              v-model="filters.type"
              :items="typeOptions"
              label="Type"
              outlined
              dense
              clearable
              @change="reloadFirstPage"
            />
          </v-col>

          <v-col
            cols="12"
            md="2"
          >
            <v-select
              v-model="pagination.perPage"
              :items="perPageOptions"
              label="Per page"
              outlined
              dense
              @change="reloadFirstPage"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-data-table
        :headers="headers"
        :items="notifications"
        :loading="loading"
        :items-per-page="pagination.perPage"
        :server-items-length="pagination.total"
        :page.sync="pagination.page"
        hide-default-footer
        class="elevation-0"
        @click:row="openNotification"
      >
        <template v-slot:item.is_read="{ item }">
          <v-chip
            small
            :color="item.is_read ? 'success darken-1' : 'red'"
            :text-color="item.is_read ? 'white' : 'white'"
          >
            {{ item.is_read ? 'Read' : 'Unread' }}
          </v-chip>
        </template>

        <template v-slot:item.type="{ item }">
          <v-chip
            small
            color="blue darken-1"
            text-color="white"
          >
            {{ resolveTypeLabel(item) }}
          </v-chip>
        </template>

        <template v-slot:item.title="{ item }">
          <div class="font-weight-medium">
            {{ item.title }}
          </div>

          <div class="caption grey--text">
            {{ item.message }}
          </div>
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ formatDateTime(item.created_at) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            :disabled="item.is_read"
            @click.stop="markAsRead(item)"
          >
            <v-icon small>
              mdi-check
            </v-icon>
          </v-btn>

          <v-btn
            icon
            small
            @click.stop="openNotification(item)"
          >
            <v-icon small>
              mdi-open-in-new
            </v-icon>
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="pa-6 text-center grey--text">
            No notifications found.
          </div>
        </template>
      </v-data-table>

      <v-divider />

      <v-card-actions class="d-flex align-center">
        <div class="caption grey--text">
          Total: {{ pagination.total }}
          <span v-if="unreadCount > 0">
            | Unread: {{ unreadCount }}
          </span>
        </div>

        <v-spacer />

        <v-pagination
          v-model="pagination.page"
          :length="pagination.lastPage"
          :total-visible="7"
          @input="fetchNotifications"
        />
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import ApiService from '@/core/services/api.service'

export default {
  name: 'NotificationsIndex',

  data() {
    return {
      loading: false,
      searchTimeout: null,
      notifications: [],
      unreadCount: 0,
      filters: {
        search: '',
        status: null,
        type: null,
      },
      pagination: {
        page: 1,
        lastPage: 1,
        perPage: 10,
        total: 0,
      },
      perPageOptions: [
        10,
        25,
        50,
        100,
      ],
      statusOptions: [
        {
          text: 'All',
          value: null,
        },
        {
          text: 'Unread',
          value: 'unread',
        },
        {
          text: 'Read',
          value: 'read',
        },
      ],
      typeOptions: [
        {
            text: 'All',
            value: null,
        },
        {
          text: 'Invoice paid',
          value: 'sales_order_document_paid',
        },
      ],
      headers: [
        {
          text: 'Status',
          value: 'is_read',
          sortable: false,
          width: 110,
        },
        {
          text: 'Type',
          value: 'type',
          sortable: false,
          width: 180,
        },
        {
          text: 'Notification',
          value: 'title',
          sortable: false,
        },
        {
          text: 'Date',
          value: 'created_at',
          sortable: false,
          width: 180,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'right',
          width: 110,
        },
      ],
    }
  },

  created() {
    this.fetchNotifications()
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    async fetchNotifications() {
      this.loading = true

      try {
        const response = await ApiService.get('notifications', this.buildQueryString())
        const data = response.data || {}
        const pagination = data.pagination || {}

        this.notifications = data.notifications || []
        this.unreadCount = data.unread_count || 0

        this.pagination.page = pagination.current_page || 1
        this.pagination.lastPage = pagination.last_page || 1
        this.pagination.perPage = pagination.per_page || this.pagination.perPage
        this.pagination.total = pagination.total || 0
      } catch (error) {
        console.error('Notifications fetch error', error)
      } finally {
        this.loading = false
      }
    },

    buildQueryString() {
      const params = []

      params.push(`page=${this.pagination.page}`)
      params.push(`per_page=${this.pagination.perPage}`)

      if (this.filters.search) {
        params.push(`search=${encodeURIComponent(this.filters.search)}`)
      }

      if (this.filters.status) {
        params.push(`status=${encodeURIComponent(this.filters.status)}`)
      }

      if (this.filters.type) {
        params.push(`type=${encodeURIComponent(this.filters.type)}`)
      }

      return `?${params.join('&')}`
    },

    reloadFirstPage() {
      this.pagination.page = 1
      this.fetchNotifications()
    },

    handleSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.reloadFirstPage()
      }, 400)
    },

    handleSearchClear() {
      this.filters.search = ''
      this.reloadFirstPage()
    },

    async openNotification(notification) {
        if (this.hasSelectedText()) {
            return
        }

      await this.markAsRead(notification)

      const data = notification.data || {}
      const url = data.url

      if (url) {
        this.$router.push(url)
      }
    },

    async markAsRead(notification) {
      if (notification.is_read) {
        return
      }

      try {
        await ApiService.post(`notifications/${notification.id}/read`, {})

        notification.is_read = true

        if (this.unreadCount > 0) {
          this.unreadCount -= 1
        }
      } catch (error) {
        console.error('Notification mark as read error', error)
      }
    },

    async markAllAsRead() {
      try {
        await ApiService.post('notifications/read-all', {})

        this.notifications = this.notifications.map((notification) => {
          return {
            ...notification,
            is_read: true,
          }
        })

        this.unreadCount = 0
      } catch (error) {
        console.error('Notifications mark all as read error', error)
      }
    },

    resolveTypeLabel(item) {
      if (item.type === 'sales_order_document_paid') {
        return item.title ?? 'Invoice paid';
      }

      return item.type
    },

    formatDateTime(value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleString()
    },

    hasSelectedText() {
        const selection = window.getSelection()

        if (!selection) {
            return false
        }

        return selection.toString().trim().length > 0
    },
  },
}
</script>